import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuthState } from '../hooks/useAuthState';
import { useCollection } from '../hooks/useCollection';
import toast from 'react-hot-toast';
import { quotes as defaultQuotes } from '../data/quotes';

interface Quote {
  id: string;
  quote: string;
  author: string;
  category: string;
}

interface QuotesContextType {
  dailyQuote: Quote | null;
  favoriteQuotes: Quote[];
  addToFavorites: (quote: Omit<Quote, 'id'>) => Promise<void>;
  removeFromFavorites: (quoteId: string) => Promise<void>;
  loading: boolean;
}

const QuotesContext = createContext<QuotesContextType | undefined>(undefined);

export const QuotesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dailyQuote, setDailyQuote] = useState<Quote | null>(null);
  const [favoriteQuotes, setFavoriteQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuthState();
  const favoriteQuotesCollection = useCollection('favorite_quotes');

  // Get quote based on day of year
  useEffect(() => {
    const getDailyQuote = () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), 0, 0);
      const diff = now.getTime() - start.getTime();
      const oneDay = 1000 * 60 * 60 * 24;
      const dayOfYear = Math.floor(diff / oneDay);
      
      // Use modulo to cycle through available quotes
      const index = dayOfYear % defaultQuotes.length;
      const quote = defaultQuotes[index];
      // Use a unique ID that includes the year to avoid key conflicts
      return { ...quote, id: `daily-${now.getFullYear()}-${dayOfYear}` };
    };

    setDailyQuote(getDailyQuote());
  }, []);

  // Load favorite quotes
  useEffect(() => {
    const loadFavorites = async () => {
      if (!user) {
        setFavoriteQuotes([]);
        setLoading(false);
        return;
      }

      try {
        const favorites = await favoriteQuotesCollection.getWhere('user_id', 'eq', user.id);
        if (favorites && Array.isArray(favorites) && favorites.length > 0) {
          // Type guard to ensure we have valid Quote objects
          const validFavorites = favorites.filter(fav => 
            fav && typeof fav === 'object' && 'id' in fav && 'quote' in fav
          );
          setFavoriteQuotes(validFavorites as Quote[]);
        } else {
          setFavoriteQuotes([]);
        }
      } catch (error) {
        console.error('Error loading favorite quotes:', error);
        if (error.message?.includes('Failed to fetch') || error.message?.includes('ERR_NAME_NOT_RESOLVED')) {
          console.warn('Network error detected, using empty favorites');
          setFavoriteQuotes([]);
        } else {
          // Fallback to mock data for development
          const mockFavorites = defaultQuotes
            .slice(0, 3)
            .map((quote, index) => ({
              ...quote,
              id: `favorite-${index}`,
            }));
          setFavoriteQuotes(mockFavorites);
        }
      } finally {
        setLoading(false);
      }
    };

    loadFavorites();
  }, [user]);

  const addToFavorites = async (quote: Omit<Quote, 'id'>) => {
    if (!user) {
      toast.error('Please sign in to save quotes');
      return;
    }

    try {
      const newQuote = await favoriteQuotesCollection.add({
        ...quote,
        user_id: user.id
      });
      setFavoriteQuotes(prev => [...prev, newQuote]);
      toast.success('Quote saved to favorites');
    } catch (error) {
      console.error('Error saving quote:', error);
      toast.error('Failed to save quote');
      throw error;
    }
  };

  const removeFromFavorites = async (quoteId: string) => {
    if (!user) return;

    try {
      await favoriteQuotesCollection.remove(quoteId);
      setFavoriteQuotes(prev => prev.filter(q => q.id !== quoteId));
      toast.success('Quote removed from favorites');
    } catch (error) {
      console.error('Error removing quote:', error);
      toast.error('Failed to remove quote');
      throw error;
    }
  };

  return (
    <QuotesContext.Provider value={{
      dailyQuote,
      favoriteQuotes,
      addToFavorites,
      removeFromFavorites,
      loading
    }}>
      {children}
    </QuotesContext.Provider>
  );
};

export const useQuotes = () => {
  const context = useContext(QuotesContext);
  if (context === undefined) {
    throw new Error('useQuotes must be used within a QuotesProvider');
  }
  return context;
};